import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SmartSearchModel } from 'mbs-ui-kit';
import { SmartSearchValidationState } from 'mbs-ui-kit/smart-search/models';
import { getSmartSearchTagValues } from 'mbs-ui-kit/smart-search/models/smart-search-model';
import { Tag, TagKind } from 'mbs-ui-shared@models/tag';
import { SmartSearchTemplateName } from 'mbs-ui-shared@modules/smart-search-templates/models/smart-search-templates-models';
import { TagsService } from 'mbs-ui-shared@services/tags.service';
import { BehaviorSubject, combineLatest, filter, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { ComputerTagsStore } from '../store/computerTags/computerTags.store';

@Injectable({
  providedIn: 'root'
})
export class ComputerTagsFacade {
  private computerTagsStore = inject(ComputerTagsStore);
  private tagsService = inject(TagsService);
  private injector = inject(Injector);

  private updating$ = new BehaviorSubject<boolean>(false);

  public allTags$ = toObservable(this.computerTagsStore.all);
  public selectedTag$ = toObservable(this.computerTagsStore.selectedTag);
  public tagLoaded$ = toObservable(this.computerTagsStore.loaded);
  public tagById$ = (id: number): Observable<Tag> => toObservable(this.computerTagsStore.entityById(id), { injector: this.injector });

  public loading$ = combineLatest([toObservable(this.computerTagsStore.loading), this.updating$]).pipe(
    map(([loading, updating]) => loading || updating)
  );

  loadTagById(params: { id: number; quiet?: boolean; force?: boolean }): Observable<Tag> {
    this.computerTagsStore.loadTagById(params);
    return this.tagById$(params.id);
  }

  loadAllTags({ force = false, quiet = false, calculateCount = true } = {}): Observable<Tag[]> {
    this.computerTagsStore.loadAllTags({ force, quiet, calculateCount });
    return this.allTags$;
  }

  setSelected(selected: number): Observable<Tag> {
    this.computerTagsStore.setSelected(selected);
    return this.selectedTag$;
  }

  create(tag: Partial<Tag>): Observable<Tag> {
    return this.upsert(tag);
  }

  update(tag: Tag): Observable<Tag> {
    return this.upsert(tag, true);
  }

  delete(id: number): Observable<null> {
    this.updating$.next(true);
    return this.tagsService.deleteTag(id).pipe(
      tap(() => this.computerTagsStore.deleteTag(id)),
      finalize(() => this.updating$.next(false))
    );
  }

  isTagsFilterValid(search: SmartSearchModel): Observable<SmartSearchValidationState> {
    return of(getSmartSearchTagValues(SmartSearchTemplateName.Tag, search)).pipe(
      switchMap((searchTags) =>
        searchTags.length
          ? this.tagLoaded$.pipe(
              filter(Boolean),
              switchMap(() => this.allTags$),
              take(1),
              map((allTags) => allTags.map((tag) => tag.name.toLowerCase())),
              map((allTags) => searchTags.every((tagName) => allTags.includes(tagName.toLowerCase()))),
              map((result) => (result ? SmartSearchValidationState.Valid : SmartSearchValidationState.Invalid))
            )
          : of(SmartSearchValidationState.Unknown)
      )
    );
  }

  private upsert(tag: Partial<Tag>, isUpdate = false): Observable<Tag> {
    this.updating$.next(true);
    return (isUpdate ? this.tagsService.updateTag(tag) : this.tagsService.createTag(tag)).pipe(
      tap((result) => this.computerTagsStore.setTags([result])),
      finalize(() => this.updating$.next(false))
    );
  }

  getTagsByKind(kind: TagKind, calculateCount = true): Observable<Tag[]> {
    return this.tagsService.getTagsByKind(kind, calculateCount);
  }
}
